import {
  DatePicker,
  feedbackcard,
  FilterQuery,
  Unifree,
  YesObject
} from '@yes.technology/react-toolkit'
import { resolveQuery } from 'filteraction/FilteractionResolver'
import { isEmpty } from 'lodash'
import { Ref, useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { AllowedDateFormats } from 'shared/types/fieldSettings.types'
import {
  isSituationField,
  isSpecialRelationFieldByColumnJson
} from 'shared/utils/specialRelationFields/specialRelationFields'
import { FieldType } from '../../shared/fields'
import {
  DataFieldLayout,
  FieldValues,
  FilterField,
  FilterFieldLayout,
  GroupedFieldValues
} from '../../types'
import Operators, { operators } from '../Operators/Operators'
import AutocompleteMultiselectionField from './AutocompleteMultiselectionField'
import AutocompleteUniselectionField from './AutocompleteUniselectionField/AutocompleteUniselectionField'
import RelationDataField from './RelationDataField'
import StandardMultiselectionField from './StandardMultiselectionField'
import StandardUniselectionField from './StandardUniselectionField'
import {
  ResolveFilterActionButton,
  StyledField,
  StyledFieldContainer
} from './styles'

export function getDefaultDataFieldLayout(
  type?: FieldType,
  columnJson?: string
): DataFieldLayout {
  if (columnJson && isSituationField(columnJson)) {
    return {
      component: 'StandardMultiselection',
      filteraction: window.situationFilterFilteractionUuid
    }
  }

  if (columnJson && isSpecialRelationFieldByColumnJson(columnJson)) {
    return {
      component: 'AutocompleteUniselection'
    }
  }

  switch (type) {
    case 'DATETIME':
      return { component: 'DatePicker' }
    case 'RELATION':
      return { component: 'AutocompleteMultiselection' }
    case 'UUID':
      return { component: 'Unifree' }
    default:
      return { component: 'Unifree' }
  }
}

type DataFieldProps = {
  id?: string
  name?: string
  label?: string
  disabled?: boolean
  type?: FieldType
  inputRef: Ref<HTMLInputElement>
  data: FieldValues['']
  objectclassUuid: string
  onChangeValues: (values: FieldValues['']['value']) => void
  uuidCurrentSitemodelObjectclass?: string
  groupedFieldValues: GroupedFieldValues
  fieldLayout?: FilterFieldLayout
  interdependentFilteringQuery?: FilterQuery
  dateFormat?: AllowedDateFormats
  groupPosition: number
  operator?: string
}

const DataField = ({
  id,
  name,
  label: defaultLabel,
  disabled,
  type,
  inputRef,
  data,
  objectclassUuid,
  onChangeValues,
  uuidCurrentSitemodelObjectclass,
  groupedFieldValues,
  fieldLayout,
  interdependentFilteringQuery,
  dateFormat = 'date-time',
  groupPosition,
  operator
}: DataFieldProps) => {
  const {
    datafield: dataFieldLayout = {},
    display_actions: displayActions = false
  } = fieldLayout || {}

  const { label: layoutLabel } = dataFieldLayout
  const label = layoutLabel || defaultLabel

  const commonProps = {
    id,
    name,
    label,
    autoComplete: 'off',
    style: {
      paddingLeft: 48
    },
    heightClass: 'regular' as const,
    disabled
  }

  const defaultDataFieldLayout = getDefaultDataFieldLayout(type, name)

  const { component: defaultComponent, filteraction: defaultFilteraction } =
    defaultDataFieldLayout

  const { component: layoutComponent, filteraction: layoutFilteraction } =
    dataFieldLayout || {}

  const component = layoutComponent || defaultComponent
  const filteraction = layoutFilteraction || defaultFilteraction

  if (!component || !data) {
    return null
  }

  if (component === 'DatePicker') {
    const currentYear = new Date().getFullYear()
    const minimumYear = currentYear - 50

    return (
      <DatePicker
        {...commonProps}
        ref={inputRef}
        value={data.value as string}
        onChange={onChangeValues}
        format={dateFormat}
        minimumYear={minimumYear}
        maximumYear={currentYear}
      />
    )
  }

  if (component === 'Unifree' || operator === 'filteraction') {
    return (
      <Unifree
        {...commonProps}
        ref={inputRef}
        value={data.value as string}
        onChange={onChangeValues}
      />
    )
  }

  if (component === 'AutocompleteMultiselection') {
    return (
      <RelationDataField
        {...{
          name,
          objectclassUuid,
          groupedFieldValues,
          displayActions,
          groupPosition,
          selectedOptions: Array.isArray(data.value) ? data.value : []
        }}
      >
        <AutocompleteMultiselectionField
          {...commonProps}
          {...{
            inputRef,
            objectclassUuid,
            selectedOptions: Array.isArray(data.value)
              ? data.value.map(({ des, uuid }) => ({ id: uuid, value: des }))
              : [],
            onChangeValues,
            interdependentFilteringQuery
          }}
        />
      </RelationDataField>
    )
  }

  if (component === 'AutocompleteUniselection') {
    return (
      <RelationDataField
        {...{
          name,
          objectclassUuid,
          groupedFieldValues,
          displayActions,
          groupPosition,
          selectedOptions: Array.isArray(data.value) ? data.value : []
        }}
      >
        <AutocompleteUniselectionField
          {...commonProps}
          {...{
            inputRef,
            objectclassUuid,
            onChangeValues,
            uuidCurrentSitemodelObjectclass,
            selectedOptions: Array.isArray(data.value) ? data.value : []
          }}
        />
      </RelationDataField>
    )
  }

  if (component === 'StandardUniselection') {
    if (!filteraction) {
      console.error('StandardUniselection must always have a filteraction')
      return null
    }

    return (
      <StandardUniselectionField
        {...commonProps}
        filteractionUuid={filteraction}
        {...{
          inputRef,
          onChangeValues,
          objectclassUuid,
          uuidCurrentSitemodelObjectclass,
          selectedOptions: Array.isArray(data.value) ? data.value : []
        }}
      />
    )
  }

  if (component === 'StandardMultiselection') {
    if (!filteraction) {
      console.error('StandardMultiselection must always have a filteraction')
      return null
    }

    return (
      <StandardMultiselectionField
        {...commonProps}
        filteractionUuid={filteraction}
        {...{
          inputRef,
          onChangeValues,
          objectclassUuid,
          uuidCurrentSitemodelObjectclass,
          selectedOptions: Array.isArray(data.value) ? data.value : []
        }}
      />
    )
  }

  return null
}

type FieldProps = {
  fieldLayout?: FilterFieldLayout
  props?: FilterField
  disabled?: boolean
  fieldValues: FieldValues
  handleChange?: (value: FieldValues) => void
  uuidCurrentSitemodelObjectclass?: string
  groupedFieldValues: GroupedFieldValues
  interdependentFilteringQuery?: FilterQuery
  dateFormat?: AllowedDateFormats
  groupPosition: number
}

const Field = ({
  fieldLayout,
  props,
  fieldValues,
  handleChange,
  uuidCurrentSitemodelObjectclass,
  groupedFieldValues,
  interdependentFilteringQuery,
  disabled,
  dateFormat,
  groupPosition
}: FieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  if (!props?.type || !props?.name || !Object.keys(fieldValues).length) {
    return null
  }

  const { uuid, name, objectclassUuid = '' } = props
  const fieldData = fieldValues[name]

  const onChangeValues: DataFieldProps['onChangeValues'] = (value) => {
    handleChange?.({
      [name]: {
        ...fieldData,
        value
      }
    })
  }

  const onChangeOperator = (operator: FieldValues['']['operator']) => {
    // inputRef.current?.focus()
    let value = fieldData.value

    if (operator === 'filteraction') {
      value = ''
    }

    handleChange?.({
      [name]: {
        ...fieldData,
        value,
        operator
      }
    })
  }

  const onResolveFilterQuery = async () => {
    const inputValue = String(fieldValues[name]?.value) || ''

    try {
      const resolvedQuery = await resolveQuery(
        {
          $filteraction: inputValue
        },
        console.log,
        [],
        true,
        true,
        (objects) => objects.map((object: YesObject) => object)
      )

      if (
        resolvedQuery &&
        !isEmpty(resolvedQuery) &&
        Object.keys(resolvedQuery).some((operator) =>
          ['$in', '$nin'].includes(operator)
        )
      ) {
        const [[operator, values]] = Object.entries(resolvedQuery) as [
          string,
          YesObject[]
        ][]

        const operatorObject = Object.values(operators)
          .flat()
          .find((op) => op?.operator === operator.replace('$', ''))

        if (!!operatorObject?.operator) {
          handleChange?.({
            [name]: {
              ...fieldValues[name],
              value: values.map((option) => ({
                uuid: option?.uuid,
                des: option?.des || option?.code || ''
              })) as FieldValues['']['value'],
              operator: operatorObject?.operator
            }
          })
        }
      }
    } catch (error) {
      console.error('Error fetching filteraction', error)

      feedbackcard(t('generic-error'), {
        type: 'error',
        message: t('filter-fields.invalid-filter-action-format')
      })
    }
  }

  const currentOperator = fieldValues[name]?.operator || ''

  return (
    <StyledFieldContainer fieldLayout={fieldLayout} key={uuid}>
      <StyledField>
        <DataField
          {...props}
          inputRef={inputRef}
          data={fieldData}
          operator={currentOperator}
          {...{
            fieldLayout,
            objectclassUuid,
            onChangeValues,
            uuidCurrentSitemodelObjectclass,
            groupedFieldValues,
            interdependentFilteringQuery,
            disabled,
            dateFormat,
            groupPosition
          }}
        />
        <Operators
          currentOperator={fieldValues[name]?.operator}
          type={fieldData?.type === 'RELATION' ? 'array' : 'string'}
          handleChange={onChangeOperator}
        />
      </StyledField>
      {currentOperator && currentOperator === 'filteraction' && (
        <ResolveFilterActionButton
          onClick={onResolveFilterQuery}
          variant='secondary'
          aria-label={t('filter-fields.resolve-filter-action')}
        >
          R
        </ResolveFilterActionButton>
      )}
    </StyledFieldContainer>
  )
}

export default Field
